import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

interface UpdateOptions {
  /**
   * Replace the current history entry instead of pushing a new one.
   *
   * Used while typing: one entry per keystroke buries the previous screen under
   * a dozen entries and makes Back useless.
   */
  replace?: boolean | undefined;
  /** Keep the current page. Only paging itself sets this. */
  keepPage?: boolean | undefined;
}

interface UrlState {
  params: URLSearchParams;
  /** Applies an arbitrary mutation to the query string. */
  update: (mutate: (next: URLSearchParams) => void, options?: UpdateOptions) => void;
  /** Sets a param, or removes it when the value is empty or equals `defaultValue`. */
  setParam: (key: string, value: string | undefined, options?: UpdateOptions) => void;
  /** Flips a `?key=true` flag on or off. */
  toggleParam: (key: string, options?: UpdateOptions) => void;
  /** Removes several params at once. */
  clearParams: (keys: readonly string[], options?: UpdateOptions) => void;
}

/**
 * Filter and pagination state held in the query string.
 *
 * An operational view has to be link-addressable — "look at this overdue queue"
 * should be a URL you can paste into a message — and it has to survive a
 * refresh and step properly under the back button.
 *
 * Every mutation except paging drops `pageParam`, because page 5 of a filter
 * you just changed is meaningless. The rule lives in one place so no
 * individual mutator can forget it.
 */
export function useUrlState(pageParam = 'page'): UrlState {
  const [params, setParams] = useSearchParams();

  const update = useCallback(
    (mutate: (next: URLSearchParams) => void, { replace = false, keepPage = false } = {}) => {
      const next = new URLSearchParams(params);
      mutate(next);
      if (!keepPage) next.delete(pageParam);
      setParams(next, { replace });
    },
    [params, setParams, pageParam],
  );

  const setParam = useCallback(
    (key: string, value: string | undefined, options?: UpdateOptions) =>
      update((next) => {
        if (value) next.set(key, value);
        else next.delete(key);
      }, options),
    [update],
  );

  const toggleParam = useCallback(
    (key: string, options?: UpdateOptions) =>
      update((next) => {
        if (next.get(key) === 'true') next.delete(key);
        else next.set(key, 'true');
      }, options),
    [update],
  );

  const clearParams = useCallback(
    (keys: readonly string[], options?: UpdateOptions) =>
      update((next) => {
        for (const key of keys) next.delete(key);
      }, options),
    [update],
  );

  return useMemo(
    () => ({ params, update, setParam, toggleParam, clearParams }),
    [params, update, setParam, toggleParam, clearParams],
  );
}
